import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Truck,
  Home,
  CheckCircle,
  Camera,
  Bell,
  MessageCircle,
  MapPin,
  Warehouse,
  Landmark,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';

gsap.registerPlugin(ScrollTrigger);

// The real per-item journey shown in the Mi Envío Cuba app
const journeyStages = [
  { icon: Package, title: 'Nuevo' },
  { icon: Warehouse, title: 'Almacén USA' },
  { icon: Truck, title: 'Enviado' },
  { icon: Landmark, title: 'En Aduana' },
  { icon: Warehouse, title: 'Almacén Cuba' },
  { icon: Home, title: 'En Distribución' },
  { icon: CheckCircle, title: 'Entregado' },
];

const appFeatures = [
  {
    icon: MapPin,
    title: 'Rastreo artículo por artículo',
    description:
      'Cada cosa que envías avanza por su propia línea de tiempo, con la fecha de cada etapa. No un estado genérico: el recorrido real de tu envío.',
  },
  {
    icon: Camera,
    title: 'Prueba de entrega verificada',
    description:
      'Foto, firma del receptor y ubicación GPS en cada entrega, visibles en tu teléfono. La confianza no se promete: se demuestra.',
  },
  {
    icon: Bell,
    title: 'Notificaciones en cada etapa',
    description:
      'Recibe un aviso en tu teléfono cada vez que tu envío cambia de estado — de la agencia hasta la puerta de tu familia.',
  },
  {
    icon: MessageCircle,
    title: 'Chat directo con tu agencia',
    description:
      'Cada envío tiene su propio chat. Si algo se complica, lo ves con el siguiente paso claro y lo resuelves ahí mismo.',
  },
];

export function Tracking() {
  const headerRef = useRef<HTMLDivElement>(null);
  const journeyRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      [headerRef, journeyRef, featuresRef].forEach((ref) => {
        gsap.fromTo(
          ref.current,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: ref.current,
              start: 'top 80%',
            },
          }
        );
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <main className="bg-bg-light min-h-screen">
      <SEO
        title="Rastrea tu Envío a Cuba desde la App | Mi Envío Cuba"
        description="Sigue cada artículo de tu envío a Cuba en tiempo real desde la app Mi Envío Cuba: línea de tiempo por etapa, notificaciones y prueba de entrega verificada con foto, firma y GPS."
        path="/rastreo"
        keywords="rastrear envío Cuba, seguimiento paquete Cuba, tracking envío Cuba, estado envío Cuba"
      />

      {/* Header */}
      <div ref={headerRef} className="pt-36 sm:pt-44 pb-12 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] text-text-primary mb-6">
            Rastrea tu envío desde la app.
          </h1>
          <p className="text-text-secondary mb-8">
            El rastreo de tus envíos vive en la app Mi Envío Cuba: eliges tu
            agencia y sigues cada artículo en tiempo real — con prueba de
            entrega verificada al final del camino.
          </p>
          <Link to="/clientes">
            <Button className="h-14 px-8 rounded-full bg-brand text-white font-medium hover:opacity-90 transition-opacity">
              Conoce la app
            </Button>
          </Link>
          <p className="text-text-muted text-sm mt-4">
            Gratis y en español — muy pronto en App Store y Google Play.
          </p>
        </div>
      </div>

      {/* The real journey */}
      <div ref={journeyRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-[28px] shadow-card p-6 lg:p-8">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-2 text-center">
              Lo que ves en la app
            </h2>
            <p className="text-text-secondary text-sm text-center mb-8">
              Cada artículo recorre estas etapas, con su fecha y su estado al
              día.
            </p>
            <div className="flex flex-wrap justify-center gap-x-2 gap-y-4">
              {journeyStages.map((stage, index) => (
                <div key={stage.title} className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-2 w-24">
                    <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center">
                      <stage.icon className="w-5 h-5 text-brand" />
                    </div>
                    <span className="text-xs font-medium text-text-secondary text-center">
                      {stage.title}
                    </span>
                  </div>
                  {index < journeyStages.length - 1 && (
                    <div className="hidden lg:block w-4 h-0.5 bg-gray-200 -mt-6" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-text-muted text-xs text-center mt-8">
              Los tiempos de entrega son estimados que define tu agencia y
              pueden variar según el método de envío y la aduana.
            </p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div ref={featuresRef} className="pb-24 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            {appFeatures.map((feature) => (
              <div
                key={feature.title}
                className="bg-white rounded-[28px] shadow-card p-6"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-text-secondary">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-text-secondary mb-4">
              ¿Listo para ver tus envíos en tiempo real?
            </p>
            <Link to="/clientes">
              <Button className="h-14 px-8 rounded-full bg-brand text-white font-medium hover:opacity-90 transition-opacity">
                Descubre Mi Envío Cuba
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
